import { Flex, Text } from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { LineSheetSet } from "@/features/line-sheet-sets/line-sheet-set.types";

// 화면 용어
//  - 권장주문금액: 라인시트세트의 MOQ. 이보다 작게 주문하면 거절될 수 있다(안내만 한다).
//  - 최소주문금액: 제출 하한(유로 5,000). 이보다 작으면 제출할 수 없다.
// 스탁오더·시즌오더 모두 적용한다. 시트 정보 패널과 제출 모달이 같은 판정을 쓴다.
export function getOrderMoq(
  lineSheetSet?: Pick<LineSheetSet, "moq"> | null
): number | null {
  if (!lineSheetSet) {
    return null;
  }
  // 0 은 "MOQ 없음"이다. 유로 세트는 서버가 비었거나 0 인 MOQ 를 5,000 으로 저장한다.
  return lineSheetSet.moq || null;
}

// 제출을 막는 하한(최소주문금액). 세트 MOQ 가 더 커도(예: 10,000) 막는 선은 5,000 유로까지고, 그 위는 안내만 한다.
// 유로가 아닌 세트는 5,000 을 환산하지 않고 세트 MOQ 를 그대로 쓴다. (서버 get_submit_block_amount 와 같은 규칙)
// 소량 주문 가능 세트(canOrderByUnit)는 단품도 받는 세트라 하한이 없고 MOQ 안내도 하지 않는다 — checkOrderAmount 참고.
const SUBMIT_BLOCK_AMOUNT_EUR = 5000;

export function getSubmitBlockAmount(
  moq?: number | null,
  currency?: string | null
): number | null {
  if (!moq) {
    return null;
  }
  return (currency ?? "EUR") === "EUR"
    ? Math.min(moq, SUBMIT_BLOCK_AMOUNT_EUR)
    : moq;
}

// 통화의 소수 자릿수(원화 0, 유로 2) — 부족 금액을 화면 표시 단위로 맞출 때 쓴다.
function fractionDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en", {
        style: "currency",
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch (e) {
    return 2;
  }
}

// 제출 모달이 어느 화면을 띄울지.
//  - BLOCKED: 최소주문금액 미만 → 제출 불가 안내(부족 금액과 패스픽 유도)
//  - BELOW_RECOMMENDED: 최소주문금액은 넘겼지만 권장주문금액 미만 → 기존 모달 + 권장주문금액 안내
//  - OK: 권장주문금액 이상(또는 MOQ 없음) → 기존 모달, 안내 없음
export type OrderAmountStage = "BLOCKED" | "BELOW_RECOMMENDED" | "OK";

export interface OrderAmountCheck {
  stage: OrderAmountStage;
  // 최소주문금액. MOQ 가 없으면 null.
  minimumAmount: number | null;
  // 최소주문금액까지 모자란 금액. 주문금액을 알 수 없으면("-") undefined.
  shortfall: number | undefined;
}

export function checkOrderAmount(
  // 합계 행과 같은 값(수수료 반영). 주문이 없으면 undefined, 금액을 못 구했으면 "-".
  totalAmount: number | "-" | undefined,
  moq?: number | null,
  currency?: string | null,
  // 소량 주문 가능 세트인지(helpers/can-order-by-unit). true 면 제출을 막지 않고 권장주문금액 안내도 하지 않는다
  // (서버 _ensure_moq_satisfied 와 같은 규칙).
  canOrderByUnit: boolean = false
): OrderAmountCheck {
  const minimumAmount = getSubmitBlockAmount(moq, currency);
  if (!moq || minimumAmount === null || canOrderByUnit) {
    return { stage: "OK", minimumAmount: null, shortfall: 0 };
  }
  if (totalAmount === "-") {
    return { stage: "BLOCKED", minimumAmount, shortfall: undefined };
  }
  const amount = totalAmount ?? 0;
  const unit = 10 ** fractionDigits(currency ?? "EUR");
  const shortfall =
    Math.max(0, Math.round(minimumAmount * unit) - Math.round(amount * unit)) /
    unit;
  if (shortfall > 0) {
    return { stage: "BLOCKED", minimumAmount, shortfall };
  }
  return {
    stage: amount < moq ? "BELOW_RECOMMENDED" : "OK",
    minimumAmount,
    shortfall: 0,
  };
}

interface MoqNoticeBannerProps {
  moq: number;
  // 라인시트세트 통화. 없으면 기본(EUR)으로 표시한다.
  currency?: string | null;
  // 최소주문금액(제출 하한) 줄도 보여줄지. 제출 모달은 이미 하한을 넘긴 주문에만 이 배너를 띄우므로 끈다.
  showMinimum?: boolean;
}

export default function MoqNoticeBanner({
  moq,
  currency,
  showMinimum = true,
}: MoqNoticeBannerProps) {
  const { t, tCurrencyString } = useI18nHelper();
  const minimumAmount = getSubmitBlockAmount(moq, currency);
  // 세트 MOQ 가 제출 하한보다 클 때만 '권장'이 따로 있다(같으면 최소주문금액 하나뿐이다).
  const hasRecommended = minimumAmount === null || moq > minimumAmount;
  const showsMinimum = showMinimum && minimumAmount !== null;
  const format = (amount: number) =>
    tCurrencyString(amount, { type: "SYMBOL", code: currency ?? undefined });

  if (!hasRecommended && !showsMinimum) {
    return null;
  }

  return (
    <Flex
      flexDirection={"column"}
      padding={"12px"}
      gap={"4px"}
      background={"#EBF4FF"}
      borderRadius={"6px"}
      color={"#00417D"}
    >
      {hasRecommended && (
        <Text textStyle={"body-sm-bold"}>
          {t("moq_notice.recommended", { amount: format(moq) })}
        </Text>
      )}
      {showsMinimum && (
        <Text textStyle={"body-sm-bold"}>
          {t("moq_notice.minimum", { amount: format(minimumAmount) })}
        </Text>
      )}
      {showsMinimum && <Text fontSize={"12px"}>{t("moq_notice.blocked")}</Text>}
      {hasRecommended && (
        <Text fontSize={"12px"}>{t("moq_notice.warning")}</Text>
      )}
    </Flex>
  );
}
